import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { Divider } from "$root/components/Divider";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { StackingContext, Text } from "@mdotm/shadcn/legacy/components";
import { useDraggable } from "@mdotm/shadcn/legacy/headless";
import { useDrivenState, useRefProxy, useUniqueDOMId, type NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { clamp, isBetween } from "@mdotm/shadcn/legacy/utils";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import { Set } from "immutable";
import { type ForwardedRef, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { renderToString } from "react-dom/server";
import { dimmedSourceOpacity, keyMetricThresholdColor } from "./categorical-palette";
import {
	ChartCardShell,
	ChartTitle,
	chartHeightInPx,
	ChartWallSwitch,
	navigatorSourceClassName,
	type ChartHeight,
	type ChartWallStatus,
} from "./ChartCardShell";

/**
 * Frequency: the charted metric in bins on the X axis, number of portfolios on the Y axis.
 *
 * Plain column series over **backend-provided** bins. Deliberately *not* Highcharts' `histogram` series
 * type (from the already-registered `histogram-bellcurve` module): that re-bins client side and would
 * fight the server, which bins over the whole predicate result rather than the page we fetched.
 *
 * Axis and tooltip labels come from the bins themselves and from the `binning` echo, never from the
 * requested bin count/width — the backend clamps and rounds even under `COUNT` / `WIDTH`.
 *
 * Clicking a column selects the portfolios that bin counts, and dragging a horizontal band selects every
 * portfolio in the covered bins (FR-2.3). That is the one piece of `MetricComparisonChart`'s interaction
 * logic worth reusing, but 1-D and keyed off bins rather than per-point rects: a bar here stands for a
 * *set* of portfolios, so a click takes or drops the whole set — see {@link selectionAfterBinClick}.
 */

export type FrequencyChartBin = {
	from: number;
	to: number;
	/** Half-open `[from, to)`; the last bin is inclusive `[from, to]`. Produced by the mapper. */
	label: string;
	/** `[CURRENT, PROPOSAL]`. */
	counts: [number, number | null];
	/** `[CURRENT, PROPOSAL]` — per source, because a portfolio can land in different bins per source. */
	itemUuids: [Array<string>, Array<string> | null];
};

/**
 * The portfolios covered by an inclusive range of bin indices, for band-drag and shift-click selection
 * (FR-2.3).
 *
 * Indices come from the axis (or from a click's point index) and can overshoot the bins in either
 * direction — a band dragged past the plot, or right to left — so they are ordered and clamped here rather
 * than at every call site.
 *
 * `source` defaults to CURRENT because that is what a *band* can honestly answer for: the band is drawn
 * over the metric axis, and with the overlay on a portfolio can sit in a different bin per source, so
 * mixing both would select portfolios whose CURRENT value is outside the band the user drew. A click, by
 * contrast, lands on one specific bar, so it passes that bar's own source.
 */
export function itemUuidsInBinRange(
	bins: Array<FrequencyChartBin>,
	fromIndex: number,
	toIndex: number,
	source: 0 | 1 = 0,
): Array<string> {
	const lo = Math.max(0, Math.min(fromIndex, toIndex));
	const hi = Math.min(bins.length - 1, Math.max(fromIndex, toIndex));
	let selected = Set<string>();
	for (let i = lo; i <= hi; i++) {
		selected = selected.union(bins[i]?.itemUuids[source] ?? []);
	}
	return selected.toArray();
}

/**
 * Whether a bin's bar should read as selected — every portfolio it counts is in the selection.
 *
 * A bar here stands for a *set* of portfolios, so "selected" is only unambiguous when the set is covered
 * whole. A band drag always lands that way, covering entire bins; a selection made elsewhere (the grid, the
 * column chart, the stacked one) can cover part of a bin, and a partly-covered bar stays unpainted rather
 * than claiming more than the user picked. An empty bin counts nobody and is never selected.
 *
 * Per source, like `itemUuids` itself: with the overlay on, the same portfolio can sit in one bin under
 * CURRENT and another under PROPOSAL, so each series answers for its own bar.
 */
export function isBinFullySelected(bin: FrequencyChartBin, source: 0 | 1, selectedIds: Set<string>): boolean {
	const itemUuids = bin.itemUuids[source] ?? [];
	return itemUuids.length > 0 && itemUuids.every((uuid) => selectedIds.has(uuid));
}

/**
 * The selection a click on a bin's column lands on (FR-2.3).
 *
 * A bar here counts a *set* of portfolios rather than standing for one, so a click takes or drops the whole
 * set: it unions the bin's portfolios into the selection, and clicking a bin that is already covered whole
 * — the state {@link isBinFullySelected} paints — drops them again. That keeps click the exact inverse of
 * itself, which a "replace the selection with this bin" reading would not: a second click there would
 * re-select what it had just cleared.
 *
 * Shift-click extends from the last clicked bin to this one and *replaces* the selection with that range,
 * exactly as the range branch of `handleSelectionClick` and a band drag do — a range is a statement about
 * what the user wants selected, not an addition to it. With no anchor to extend from it falls back to a
 * plain click.
 *
 * Per source, like the bars themselves: a click identifies one specific bar, so it answers with the
 * portfolios *that* bar counts, unlike a band drag over the metric axis (see {@link itemUuidsInBinRange}).
 */
export function selectionAfterBinClick(
	bins: Array<FrequencyChartBin>,
	source: 0 | 1,
	clickedIndex: number,
	{ selectedIds, anchorIndex, shift }: { selectedIds: Set<string>; anchorIndex: number | null; shift: boolean },
): Set<string> {
	if (shift && anchorIndex != null) {
		return Set(itemUuidsInBinRange(bins, anchorIndex, clickedIndex, source));
	}
	const itemUuids = bins[clickedIndex]?.itemUuids[source] ?? [];
	const bin = bins[clickedIndex];
	if (bin && isBinFullySelected(bin, source, selectedIds)) {
		return selectedIds.subtract(itemUuids);
	}
	return selectedIds.union(itemUuids);
}

/**
 * Where a metric value falls on this chart's **categorical** X axis, as a fractional category index — or
 * `null` when it falls outside the drawn axis.
 *
 * The key-metric threshold (FR-8) is a value on the metric's scale, but the axis here is `categories`, one
 * slot per bin, so a plot line's `value` is an index rather than a metric value and the two have to be
 * translated. Highcharts centres category `i` at axis value `i`, which means its slot — and so the bin's
 * `[from, to)` span — runs from `i - 0.5` to `i + 0.5`. Hence `index - 0.5 + (threshold - from) / width`:
 * the `-0.5` puts the bin's `from` at its left edge, and the fraction walks across the slot. A threshold
 * on a bin boundary therefore lands exactly between two columns, which is where the eye expects it.
 *
 * **Out of range returns `null`, i.e. the line is omitted rather than clamped to the axis edge.** Clamping
 * would draw the line at a position it does not have: pinned to the last edge, a threshold of 90% on an
 * axis that stops at 12% looks like a threshold of 12%, and every column would read as being on the wrong
 * side of it — the one thing a threshold line exists to tell you. Omitting says less but says nothing
 * false, and it is also what {@link DensityChart} does for free, its numeric axis simply not drawing a plot
 * line outside its extremes; the two distribution kinds share a threshold, so they should not disagree
 * about when it is visible.
 */
export function thresholdCategoryIndex(bins: Array<FrequencyChartBin>, threshold: number | undefined): number | null {
	if (threshold == null || bins.length === 0) {
		return null;
	}
	const first = bins[0];
	const last = bins[bins.length - 1];
	if (threshold < first.from || threshold > last.to) {
		return null;
	}
	// The last bin is inclusive `[from, to]`, so a threshold exactly on the final edge matches no
	// half-open span and belongs to that bin's right edge.
	const found = bins.findIndex((bin) => threshold >= bin.from && threshold < bin.to);
	const index = found === -1 ? bins.length - 1 : found;
	const width = bins[index].to - bins[index].from;
	// A degenerate bin has no interior to place the value in; the slot's centre is the honest answer.
	if (!(width > 0)) {
		return index;
	}
	return index - 0.5 + (threshold - bins[index].from) / width;
}

/**
 * Name of the hidden series the navigator strip is built from — see `binHeights`. Never rendered anywhere a
 * reader can see it (no plot, no legend, no tooltip), so it is an internal handle rather than a label.
 */
const navigatorSourceSeriesName = "__navigator-bin-heights";

/**
 * An `id` per series, so that `chart.update` matches the incoming options to the live series by identity
 * rather than by position.
 *
 * Highcharts' React wrapper updates through `chart.update(options, redraw, oneToOne = true)`, which matches
 * a collection one to one by *array index* and only consults `id` when a series declares one; `Series#update`
 * then merges the incoming options over the ones the series already carried, so anything the new options
 * leave unsaid survives. The series list here is two entries long or three — `hasProposal` decides — so a
 * response that gains or loses its PROPOSAL half shifts every later slot by one, and the series that lands
 * in the navigator source's slot inherits its `visible: false`, `showInLegend: false` and `className`: a
 * source that is missing from the plot while the strip, built from `binHeights` and so still complete, keeps
 * drawing the bins it no longer plots.
 */
const seriesIds = {
	current: "current",
	proposal: "proposal",
	navigatorSource: "navigator-source",
} as const;

export type FrequencyChartProps = {
	innerRef?: ForwardedRef<HighchartsReactRefObject>;
	chartTitle: string;
	mainMetricLabel: string;
	secondaryMetricLabel: string;
	bins: Array<FrequencyChartBin>;
	/** @default false */
	hasProposal?: boolean;
	/** Dims the {@link mainMetricLabel} series so the proposal one reads as the primary one. */
	highlightSecondaryMetric?: boolean;
	/** e.g. % — the unit of the *binned metric*, i.e. of the X axis. */
	unit?: string;
	/**
	 * The key-metric threshold (FR-8), as a value on the binned metric's scale — so the line is
	 * **vertical** here, unlike the column chart's horizontal one, and has to be mapped onto the
	 * categorical axis by {@link thresholdCategoryIndex}. Read-only: it is not draggable.
	 */
	threshold?: number;
	midActions?: NodeOrFn;
	actions?: NodeOrFn;
	/** @default 'tall' */
	chartHeight?: ChartHeight;
	selectedIds?: Set<string> | null;
	onSelectionChange?(newSelection: Set<string>): void;
	disable?: boolean;
};

export function FrequencyChart(props: FrequencyChartProps): React.JSX.Element {
	const highchartsRef = useRefProxy(null, props.innerRef);
	const unit = props.unit ?? "";
	const { formatNumber } = useLocaleFormatters();
	const hasProposal = props.hasProposal ?? false;
	const chartId = useUniqueDOMId();

	const [selectedIds, setSelectedIds] = useDrivenState(props.selectedIds ?? Set<string>(), {
		equalityComparator: (a, b) => a.equals(b),
	});

	/**
	 * `onSelectionChange` at a stable identity, so `chartOptions` can depend on the props it reads instead of
	 * on `props` wholesale — see the note on that memo's dependencies. The call site's is a new function every
	 * render, and as a dependency it cost a full `chart.update(…, oneToOne: true)` each time.
	 */
	const emitSelectionChange = useEffectEvent((newSelection: Set<string>) => props.onSelectionChange?.(newSelection));
	const [band, setBand] = useState<{ left: number; width: number; top: number; height: number } | null>(null);
	const dragStateRef = useRef<{ initialSelection: Set<string>; currentSelection: Set<string> } | null>(null);
	/** Which bin a shift-click extends from — the last clicked bin, or the trailing edge of the last band. */
	const shiftSelectionBinIndexRef = useRef<number | null>(null);

	const { dragging } = useDraggable({
		draggableEl: props.disable ? undefined : window,
		filter(initialCoords) {
			const cardAreaEl = document.getElementById(chartId);
			const container = cardAreaEl?.querySelector(".highcharts-container");
			if (!container) {
				return false;
			}
			const box = container.getBoundingClientRect();
			// The navigator strip is a control, not plot: a drag that starts on it is Highcharts' own pan/zoom,
			// and letting the rubber band claim it would both select bins the user never meant and fight the
			// handle for the pointer. So the drag region stops at the navigator's top edge — the same guard
			// `StackedMetricChart` and `MetricComparisonChart` apply for the same reason. Falls back to the
			// container's bottom before the navigator has rendered (or where it cannot, e.g. jsdom).
			const navigatorTop = cardAreaEl?.querySelector(".highcharts-navigator")?.getBoundingClientRect().top;
			const dragBottom = navigatorTop ?? box.bottom;
			if (!isBetween(initialCoords.x, box.left, box.right) || !isBetween(initialCoords.y, box.top, dragBottom)) {
				return false;
			}
			dragStateRef.current = { initialSelection: selectedIds, currentSelection: selectedIds };
			return true;
		},
		preventDefault: ({ phase }) => phase !== "begin",
		stopPropagation: ({ phase }) => phase !== "begin",
		onDragMove(initial, current) {
			const chart = highchartsRef.current?.chart;
			if (!chart || !dragStateRef.current) {
				return;
			}
			const box = chart.container.getBoundingClientRect();
			// 1-D: only the x extent of the drag matters, the whole plot height is always covered.
			const [fromPx, toPx] = [Math.min(initial.x, current.x) - box.left, Math.max(initial.x, current.x) - box.left];
			if (toPx - fromPx < 2) {
				return;
			}

			setBand({
				left: box.left + clamp(fromPx, chart.plotLeft, chart.plotLeft + chart.plotWidth),
				width:
					clamp(toPx, chart.plotLeft, chart.plotLeft + chart.plotWidth) -
					clamp(fromPx, chart.plotLeft, chart.plotLeft + chart.plotWidth),
				top: box.top + chart.plotTop,
				height: chart.plotHeight,
			});

			// Categorical axis: `toValue` gives a fractional category index, which rounds to the bin whose
			// column the pointer is over.
			const axis = chart.xAxis[0];
			const [fromIndex, toIndex] = [Math.round(axis.toValue(fromPx)), Math.round(axis.toValue(toPx))];
			// The edge the drag ended on, so a following shift-click extends the band rather than restarting.
			// Clamped, unlike the range itself: an anchor is a bin the user can see, and a band dragged past
			// the plot would otherwise leave one pointing off the axis.
			shiftSelectionBinIndexRef.current = clamp(
				current.x - initial.x > 0 ? fromIndex : toIndex,
				0,
				Math.max(0, props.bins.length - 1),
			);
			const covered = Set(itemUuidsInBinRange(props.bins, fromIndex, toIndex));

			dragStateRef.current.currentSelection = covered;
			setSelectedIds(dragStateRef.current.currentSelection);
		},
		onDragEnd() {
			if (
				dragStateRef.current &&
				!dragStateRef.current.currentSelection.equals(dragStateRef.current.initialSelection)
			) {
				emitSelectionChange(dragStateRef.current.currentSelection);
			}
			dragStateRef.current = null;
			setBand(null);
		},
	});

	/**
	 * Memoized for the same reason the options are: `containerProps` is the *other* entry in the dependency
	 * list of the layout effect that calls `chart.update`, so an object literal here re-inits every series on
	 * every render however stable the options object is.
	 */
	const containerProps = useMemo(
		() => ({
			style: { height: chartHeightInPx(props.chartHeight ?? "tall"), width: "100%" },
			className: "cursor-crosshair",
		}),
		[props.chartHeight],
	);

	const chartOptions = useMemo((): Highcharts.Options => {
		// A fully covered bin wears the column chart's selected fills, so a selection reads the same wherever
		// it was made — `palette_P400` for CURRENT and the darker `palette_P700` for PROPOSAL, as there.
		const pointsFor = (source: 0 | 1, selectedColor: string) =>
			props.bins.map(
				(bin): Highcharts.PointOptionsObject => ({
					y: (source === 0 ? bin.counts[0] : bin.counts[1]) ?? 0,
					color: isBinFullySelected(bin, source, selectedIds) ? selectedColor : undefined,
				}),
			);

		// Click-to-select, per series: the bar that was clicked answers with its own source's portfolios —
		// see `selectionAfterBinClick`. Wired here rather than through `handleSelectionClick`, which keys off
		// one portfolio per category and has no notion of a bar covering a set of them.
		const selectionEventsFor = (source: 0 | 1): Highcharts.SeriesEventsOptionsObject => ({
			click(e) {
				const clickedIndex = e.point.index;
				const shift = Boolean((e as unknown as { shiftKey?: boolean }).shiftKey);
				const newSelection = selectionAfterBinClick(props.bins, source, clickedIndex, {
					selectedIds,
					anchorIndex: shiftSelectionBinIndexRef.current,
					shift,
				});
				// Like `handleSelectionClick`, a shift-click leaves the anchor where it was, so the range can be
				// widened and narrowed from the same end.
				if (!shift) {
					shiftSelectionBinIndexRef.current = clickedIndex;
				}
				// An empty bin has nobody to take or drop; it should not push an identical selection upstream.
				if (newSelection.equals(selectedIds)) {
					return;
				}
				setSelectedIds(newSelection);
				emitSelectionChange(newSelection);
			},
		});

		const series: Array<Highcharts.SeriesColumnOptions> = [
			{
				type: "column",
				id: seriesIds.current,
				name: props.mainMetricLabel,
				color: themeCSSVars.palette_S400,
				// Nothing to highlight *against* without a second series, so the dimming waits for one.
				opacity: (props.highlightSecondaryMetric ?? false) && hasProposal ? dimmedSourceOpacity : undefined,
				data: pointsFor(0, themeCSSVars.palette_P400),
				events: selectionEventsFor(0),
				// The strip reads `navigatorSourceSeriesName`, not a source — see `binHeights`.
				showInNavigator: false,
			},
		];

		if (hasProposal) {
			series.push({
				type: "column",
				id: seriesIds.proposal,
				name: props.secondaryMetricLabel,
				color: themeCSSVars.palette_B400,
				data: pointsFor(1, themeCSSVars.palette_P700),
				events: selectionEventsFor(1),
				showInNavigator: false,
			});
		}

		// The navigator's silhouette (FR-7.1): one *column* per bin, as tall as the taller of the two bars the
		// chart draws there, which is the chart in miniature. The taller of the two so a bin only the proposal
		// fills still gets a mark, and never whichever source is currently highlighted — the strip is a map of
		// the whole distribution, and having it redraw itself when "Highlight proposal" is toggled would make
		// it read as a second plot rather than as a control. Zero where a bin counts nobody: unlike a missing
		// portfolio that is a real count, so it reads as a bin with no portfolios in it rather than as a gap.
		//
		// Carried by a series of the *chart*, hidden in the plot and flagged `showInNavigator`, rather than
		// handed to the strip as `navigator.series.data`. The strip looks the same either way; what differs is
		// what `chart.update()` then does to the navigator, and `HighchartsReact` calls `chart.update()` after
		// every render. Highcharts diffs the incoming options against the live ones through `cleanRecursively`,
		// which treats *any* array as changed — it never compares element by element — so a `data` array under
		// `navigator` makes the navigator options read as dirty on every single render. `Chart.update` routes a
		// dirty `navigator` to `Navigator.update`, which patches nothing: it destroys the navigator and re-inits
		// it. The rebuilt strip re-adds its group to the renderer mid-update and lands *behind* the chart
		// background, where it stops receiving pointer events altogether — the first drag works and from then on
		// the strip is a picture: no pan, no second zoom. Keeping every value under `navigator` a primitive
		// leaves those options clean, so the live navigator — masks, handles and drag state — survives the
		// render, and `Series#afterUpdate` re-syncs the strip from this series anyway.
		const binHeights = props.bins.map((bin) => Math.max(bin.counts[0], hasProposal ? (bin.counts[1] ?? 0) : 0));
		series.push({
			type: "column",
			id: seriesIds.navigatorSource,
			name: navigatorSourceSeriesName,
			className: navigatorSourceClassName,
			data: binHeights,
			showInNavigator: true,
			// Drawn in the strip only. `visible: false` keeps it out of the plot, out of the Y axis' extremes and
			// out of Highcharts' column metrics — so the real columns keep exactly the width and offset they have
			// without it — while `navigator.series.visible` puts the strip's copy of it back on. Untrackable by
			// the pointer too, so it cannot answer a tooltip the reader aimed at a bar.
			visible: false,
			showInLegend: false,
			enableMouseTracking: false,
		});

		// The key-metric threshold, translated from a metric value into a fractional category index — see
		// `thresholdCategoryIndex`, including why an out-of-range threshold draws nothing.
		//
		// Not draggable, unlike the column chart's: that one converts a pointer delta into a value through
		// its y-axis' own pixel geometry, and a categorical axis has no metric scale to convert *back* to —
		// the only values it knows are slot indices. The threshold is edited in the settings panel here.
		const thresholdIndex = thresholdCategoryIndex(props.bins, props.threshold);
		const thresholdLine: Array<Highcharts.XAxisPlotLinesOptions> =
			thresholdIndex == null || props.threshold == null
				? []
				: [
						{
							value: thresholdIndex,
							color: keyMetricThresholdColor,
							width: 2,
							dashStyle: "Solid",
							// Over the columns, which are drawn opaque and would otherwise hide it.
							zIndex: 5,
							label: {
								text: `Threshold ${formatNumber(props.threshold, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`,
								rotation: 0,
								align: "left",
								x: 4,
								y: 12,
								style: { color: themeCSSVars.palette_N500, fontSize: "10px" },
							},
						},
					];

		return {
			chart: { type: "column", style: { fontFamily: "Gotham,sans-serif" } },
			title: { text: "" },
			credits: { enabled: false },
			// No legend: the only thing it could name here is CURRENT vs PROPOSAL, and that is what the
			// "Highlight proposal" toggle in the header says — see `chartTypeDrawsLegend`. The two series are
			// told apart by their colours, which are the platform's CURRENT/PROPOSAL pair everywhere.
			legend: { enabled: false },
			xAxis: {
				// Labels come from the bins, never from the requested binCount/binWidth.
				categories: props.bins.map((bin) => bin.label),
				title: { text: "" },
				tickWidth: 0,
				labels: {
					// Centred on the column the bin draws. A bin's label is a `[from, to)` range, so a label that
					// does not sit under its own column reads as belonging to the neighbour it is nearer to —
					// which is the one thing an axis of ranges must not get wrong.
					align: "center",
					// Upright, and staggered onto a second row once the bins outgrow the room, rather than rotated.
					// Highcharts anchors a rotated label by its *end*, so a -45° label lands a half-width to the
					// left of its own column, against the previous one; centring it instead swings the other half
					// up into the plot and over the columns. Two rows of upright labels centre exactly, cost no
					// more vertical space than the rotation did, and read without tilting the reader's head.
					rotation: 0,
					staggerLines: props.bins.length > 12 ? 2 : undefined,
				},
				plotLines: thresholdLine,
			},
			yAxis: {
				gridLineDashStyle: "Dash",
				allowDecimals: false,
				title: { text: "Portfolios" },
				labels: {
					// Through the app's formatter, like every other number these charts print — and like this
					// chart's own tooltip counts, which is what makes it more than tidiness. Highcharts'
					// `defaultLabelFormatter` omits the thousands separator below 10 000 and switches to numeric
					// symbols above it, so the axis would read "1000" beside a tooltip saying "1,234" about that
					// very column, and "10k" beside "10,000" in a large workspace. Not a rare shape either: the
					// distribution endpoint takes no `limit`, aggregating the whole predicate result rather than
					// the page the row endpoints cap at 1000.
					formatter(ctx) {
						return formatNumber(ctx.value, { maxDecimalPlaces: 0 });
					},
				},
			},
			plotOptions: {
				column: {
					// Bins are contiguous, so the columns should read as contiguous too.
					groupPadding: 0.05,
					pointPadding: 0.02,
					borderWidth: 0,
					animation: { duration: 0 },
					shadow: false,
				},
			},
			tooltip: {
				enabled: !dragging,
				useHTML: true,
				padding: 0,
				borderWidth: 0,
				shared: true,
				shadow: true,
				formatter(this) {
					const index = this.points?.[0]?.point.index ?? 0;
					const bin = props.bins[index];
					if (!bin) {
						return "";
					}
					return renderToString(
						<div className="p-2 rounded-sm bg-white min-w-60 shadow-sm flex flex-col gap-2">
							<div className="py-1 rounded-sm bg-[#EFF0F3] text-center">
								{/* The label already carries the unit on both edges (the mapper's `formatValue` appends
								    it), so appending it again here renders "[18.94%, 25.25%) %". */}
								<Text type="Body/M/Bold">{bin.label}</Text>
							</div>
							<div className="flex justify-between">
								<div className="flex items-center gap-1">
									<ColoredRectangle variant="vertical" color={themeCSSVars.palette_S400} />
									<Text type="Body/M/Book">{props.mainMetricLabel}</Text>
								</div>
								<Text type="Body/M/Bold">{formatNumber(bin.counts[0], { maxDecimalPlaces: 0 })}</Text>
							</div>
							{hasProposal && <Divider direction="horizontal" />}
							{hasProposal && (
								<div className="flex justify-between">
									<div className="flex items-center gap-1">
										<ColoredRectangle variant="vertical" color={themeCSSVars.palette_B400} />
										<Text type="Body/M/Book">{props.secondaryMetricLabel}</Text>
									</div>
									<Text type="Body/M/Bold">{formatNumber(bin.counts[1] ?? 0, { maxDecimalPlaces: 0 })}</Text>
								</div>
							)}
						</div>,
					);
				},
			},
			series,
			// FR-7.1, as on the two column charts: a fine binning puts more bins on the axis than fit, so the
			// distribution is made navigable by the strip rather than by coarsening it behind the user's back.
			// Same config as `StackedMetricChart`'s, the source series aside — see `binHeights`.
			navigator: {
				enabled: true,
				height: 30,
				// Primitives only, deliberately: an array here — a `series.data` above all — makes Highcharts
				// destroy and rebuild the navigator on every render, which leaves it inert. See `binHeights`.
				series: {
					// One mark per bin at the height the chart draws it, rather than the default `areaspline`
					// through the same points, which would curve through counts no bin has. Columns also give the
					// strip's x axis a `pointRange` of one category, the main axis' own, so the window edges land
					// on bin boundaries instead of on the first and last centres.
					type: "column",
					color: themeCSSVars.palette_S300,
					opacity: 0.7,
					// The strip inherits its source series' visibility, and that one is hidden in the plot on
					// purpose; the strip is the one place it *is* meant to be drawn.
					visible: true,
					// The default averages neighbouring points into one mark once they outnumber half the strip's
					// pixels, in *time* units, which on an axis of bins is not a grouping of anything.
					dataGrouping: { enabled: false },
				},
				xAxis: { labels: { enabled: false } },
				yAxis: { labels: { enabled: false } },
			},
			exporting: { enabled: false },
		};
		// The props this reads, one by one, rather than `props` itself — which is a fresh object every render,
		// so depending on it gave these options a new identity every time, and Highcharts' React wrapper keys its
		// `chart.update(options, redraw, oneToOne)` on exactly that identity. `useEffectEvent` results are stable
		// by construction and so are absent below.
	}, [
		props.bins,
		props.highlightSecondaryMetric,
		props.mainMetricLabel,
		props.secondaryMetricLabel,
		props.threshold,
		unit,
		formatNumber,
		hasProposal,
		dragging,
		selectedIds,
		setSelectedIds,
	]);

	/**
	 * Resets the navigator's window whenever the bins it measures change in number.
	 *
	 * The window deliberately survives a re-render — `chart.update` leaves the live navigator alone, which is
	 * what keeps the range the user picked from being thrown away every time anything above the chart renders
	 * (see `binHeights`). A *different set of bins* is the case where surviving is wrong: the extremes are
	 * indices, so a window over the tail of a long selection stays at, say, `[20, 29]` after a refetch leaves
	 * eight bins where there were thirty — the plot then draws an empty stretch past the end while the strip
	 * below it, redrawn from the new data, shows every column there is. Which is the strip and the plot
	 * disagreeing about what the chart is showing.
	 *
	 * `zoomOut` rather than a clamp: a window whose bins are gone is not a window the user chose any more.
	 * It runs in a layout effect, and `HighchartsReact`'s own update is one too — in a child, so it has
	 * already applied the new options by the time this fires. Both callers of `MetricComparisonChart` reset
	 * the zoom this way from the outside; here it belongs to the chart, so that no caller can be the one that
	 * forgets.
	 */
	const binCountRef = useRef(props.bins.length);
	useLayoutEffect(() => {
		// Nothing to reset on the first render — the window opens on the whole axis — and resetting it
		// there would only cost the fresh chart a second redraw.
		if (binCountRef.current !== props.bins.length) {
			binCountRef.current = props.bins.length;
			highchartsRef.current?.chart?.zoomOut();
		}
	}, [props.bins.length]);

	return (
		<>
			{band && (
				<StackingContext.Consumer>
					{({ zIndex }) =>
						createPortal(
							<div
								className="fixed border bg-slate-500/50 pointer-events-none"
								style={{ zIndex, left: band.left, top: band.top, width: band.width, height: band.height }}
							/>,
							document.body,
						)
					}
				</StackingContext.Consumer>
			)}
			<ChartCardShell
				id={chartId}
				fullHeight={props.chartHeight === "full"}
				title={<ChartTitle>{props.chartTitle}</ChartTitle>}
				midActions={props.midActions}
				actions={props.actions}
			>
				<HighchartsReact
					containerProps={containerProps}
					highcharts={Highcharts}
					options={chartOptions}
					ref={highchartsRef}
				/>
			</ChartCardShell>
		</>
	);
}

export function FrequencyChartWall({
	status,
	...rest
}: FrequencyChartProps & { status: ChartWallStatus }): React.JSX.Element {
	return (
		<ChartWallSwitch
			status={status}
			chartTitle={rest.chartTitle}
			midActions={rest.midActions}
			actions={rest.actions}
			chartHeight={rest.chartHeight}
		>
			{() => <FrequencyChart {...rest} />}
		</ChartWallSwitch>
	);
}
